import React, { useState, useEffect, useRef, useCallback } from 'react';
import { publicApi } from '../services/publicApi';
import './PublicQuiz.css';

const PublicQuiz = ({ letterId, onComplete, onCancel }) => {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showAnswer, setShowAnswer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [showPrepPopup, setShowPrepPopup] = useState(true);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Shuffle options deterministically
  const shuffleOptions = (options, questionId) => {
    const seed = (questionId?.toString().length || 0) + (questionId || 0);
    const shuffled = [...options];
    let currentIndex = shuffled.length;
    let temporaryValue;
    let randomIndex;

    while (currentIndex !== 0) {
      randomIndex = Math.floor(((Math.sin(seed * currentIndex) + 1) / 2) * currentIndex);
      currentIndex -= 1;
      temporaryValue = shuffled[currentIndex];
      shuffled[currentIndex] = shuffled[randomIndex];
      shuffled[randomIndex] = temporaryValue;
    }

    return shuffled;
  };

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);

    try {
      // Fill unattempted answers
      const finalAnswers = { ...answers };
      quiz.questions.forEach(q => {
        const qId = q.question_id.toString();
        if (!finalAnswers[qId]) {
          finalAnswers[qId] = 'Unattempted';
        }
      });

      const timeTaken = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : 0;

      const result = await publicApi.submitPublicQuiz(finalAnswers, timeTaken);

      // Include answers and time_taken in result for localStorage
      onComplete({
        ...result,
        answers: finalAnswers,
        time_taken: timeTaken
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, answers, quiz, onComplete]);

  // Fetch quiz on mount
  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        const data = await publicApi.getPublicQuiz();
        setQuiz(data);
        setTimeRemaining(data.time_limit_minutes * 60);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [letterId]);

  // Timer
  useEffect(() => {
    if (!quizStarted || showAnswer) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [quizStarted, showAnswer, handleSubmit]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartQuiz = () => {
    setShowPrepPopup(false);
    setQuizStarted(true);
    startTimeRef.current = Date.now();
  };

  const handleAnswerSelect = (questionId, answer) => {
    if (showAnswer) return;
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleMultiAnswerSelect = (questionId, option) => {
    if (showAnswer) return;
    setAnswers(prev => {
      const current = prev[questionId] || [];
      const updated = current.includes(option)
        ? current.filter(a => a !== option)
        : [...current, option];
      return { ...prev, [questionId]: updated };
    });
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
    clearInterval(timerRef.current);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowAnswer(false);
    }
  };

  const handleEndTest = async () => {
    await handleSubmit();
  };

  if (loading) {
    return (
      <div className="public-quiz-container">
        <div className="public-quiz-loading">
          <div className="spinner-modern"></div>
          <p>Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-quiz-container">
        <div className="public-quiz-error">
          <p>{error}</p>
          <button className="btn-modern btn-secondary-modern" onClick={onCancel}>Back to Letter</button>
        </div>
      </div>
    );
  }

  if (!quiz) return null;

  // Preparation popup
  if (showPrepPopup) {
    return (
      <div className="public-quiz-container">
        <div className="public-quiz-prep-popup">
          <div className="prep-popup-content">
            <h3><i className="bi bi-lightning-charge-fill"></i> Ready for the Quiz?</h3>
            <p>This quiz tests your understanding of Letter #{letterId}.</p>
            <ul className="prep-popup-details">
              <li><i className="bi bi-clock"></i> Time limit: {quiz.time_limit_minutes} minutes</li>
              <li><i className="bi bi-question-circle"></i> {quiz.questions.length} questions</li>
              <li><i className="bi bi-arrow-repeat"></i> Unlimited retakes</li>
            </ul>
            <div className="prep-popup-actions">
              <button className="btn-modern btn-primary-modern" onClick={handleStartQuiz}>
                Start Quiz
              </button>
              <button className="btn-modern btn-secondary-modern" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const currentQuestionId = currentQuestion.question_id.toString();
  const selectedAnswer = answers[currentQuestionId];
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;
  const shuffledOptions = shuffleOptions(currentQuestion.options, currentQuestion.question_id);

  return (
    <div className="public-quiz-container">
      <div className="public-quiz-header">
        <div className="quiz-info">
          <h3>{quiz.title}</h3>
          <span className="quiz-progress">
            Question {currentQuestionIndex + 1} of {quiz.questions.length}
          </span>
        </div>
        <div className="quiz-timer">
          <i className="bi bi-clock"></i>
          <span className={timeRemaining < 60 ? 'timer-warning' : ''}>
            {formatTime(timeRemaining)}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="quiz-progress-bar">
        <div
          className="quiz-progress-fill"
          style={{ width: `${((currentQuestionIndex + 1) / quiz.questions.length) * 100}%` }}
        ></div>
      </div>

      {/* Question */}
      <div className="public-quiz-question">
        <div className="question-meta">
          <span className={`difficulty-badge difficulty-${currentQuestion.difficulty}`}>
            {currentQuestion.difficulty}
          </span>
          {currentQuestion.question_type && (
            <span className="type-badge">{currentQuestion.question_type.replace(/_/g, ' ')}</span>
          )}
        </div>

        <p className="question-text">{currentQuestion.question_text}</p>

        {/* Code snippet */}
        {currentQuestion.code_snippet && (
          <pre className="question-code">
            <code>{currentQuestion.code_snippet}</code>
          </pre>
        )}

        {/* Options */}
        <div className="question-options">
          {shuffledOptions.map((option, idx) => {
            const optionLetter = String.fromCharCode(65 + idx);
            const isSelected = currentQuestion.multiple_answers
              ? (selectedAnswer || []).includes(option)
              : selectedAnswer === option;

            let optionClass = 'quiz-option';
            if (isSelected) optionClass += ' selected';
            if (showAnswer) {
              const correctAnswer = currentQuestion.correct_answer;
              const correctList = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];
              const isOptionCorrect = correctList.includes(option);
              if (isOptionCorrect) optionClass += ' correct';
              else if (isSelected && !isOptionCorrect) optionClass += ' incorrect';
            }

            return (
              <button
                key={idx}
                className={optionClass}
                onClick={() => currentQuestion.multiple_answers
                  ? handleMultiAnswerSelect(currentQuestionId, option)
                  : handleAnswerSelect(currentQuestionId, option)
                }
                disabled={showAnswer}
              >
                <span className="option-letter">{optionLetter}</span>
                <span className="option-text">{option}</span>
              </button>
            );
          })}
        </div>

        {/* Explanation */}
        {showAnswer && currentQuestion.explanation && (
          <div className="quiz-explanation">
            <h5>Explanation</h5>
            <p>{currentQuestion.explanation}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="public-quiz-actions">
        {!showAnswer ? (
          <button
            className="btn-modern btn-primary-modern"
            onClick={handleShowAnswer}
            disabled={!selectedAnswer}
          >
            Check Answer
          </button>
        ) : isLastQuestion ? (
          <button
            className="btn-modern btn-primary-modern"
            onClick={handleEndTest}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'End Test'}
          </button>
        ) : (
          <button
            className="btn-modern btn-primary-modern"
            onClick={handleNextQuestion}
          >
            Next Question
          </button>
        )}
        <button className="btn-modern btn-secondary-modern" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PublicQuiz;
